import {
  PublicClientApplication,
  type AuthenticationResult,
  type Configuration,
} from '@azure/msal-browser';
import { appSurfaceForPath, surfaceConfig } from '../appSurface';

const INTERACTIVE_LOGIN_PENDING_KEY = 'valentine:microsoft-interactive-login';
const completedInteractiveReauth = new WeakMap<PublicClientApplication, AuthenticationResult>();

function isRedirectOriginTrusted(redirectUri: string | undefined): redirectUri is string {
  if (!redirectUri) return false;
  try {
    return new URL(redirectUri).origin === window.location.origin;
  } catch {
    return false;
  }
}

export function markInteractiveMicrosoftLoginStarted(): void {
  try { sessionStorage.setItem(INTERACTIVE_LOGIN_PENDING_KEY, '1'); } catch { /* storage unavailable */ }
}

export function clearInteractiveMicrosoftLoginStarted(): void {
  try { sessionStorage.removeItem(INTERACTIVE_LOGIN_PENDING_KEY); } catch { /* storage unavailable */ }
}

function consumeInteractiveMicrosoftLoginStarted(): boolean {
  try {
    const pending = sessionStorage.getItem(INTERACTIVE_LOGIN_PENDING_KEY) === '1';
    sessionStorage.removeItem(INTERACTIVE_LOGIN_PENDING_KEY);
    return pending;
  } catch {
    return false;
  }
}

export function consumeInteractiveMicrosoftReauth(
  instance: PublicClientApplication,
): AuthenticationResult | null {
  const result = completedInteractiveReauth.get(instance) ?? null;
  completedInteractiveReauth.delete(instance);
  return result;
}

export interface AuthConfig {
  enabled: boolean;
  tenant_id?: string;
  client_id?: string;
  desktop_client_id?: string;
  authority?: string;
  redirect_uri?: string;
  methods?: string[];
  native?: { enabled: boolean };
  idle_minutes?: number;
  cookie_first_enabled?: boolean;
}

export interface MsalInit {
  instance: PublicClientApplication | null;
  config: AuthConfig;
}

export async function initializeMsal(): Promise<MsalInit> {
  try {
    // Fetch auth config from backend (public endpoint, no auth needed)
    const resp = await fetch('/api/auth/config');

    if (!resp.ok) {
      console.warn('Auth config fetch failed:', resp.status, resp.statusText);
      return { instance: null, config: { enabled: false } };
    }

    const contentType = resp.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      console.warn('Auth config returned non-JSON response:', contentType);
      return { instance: null, config: { enabled: false } };
    }

    const config: AuthConfig = await resp.json();

    // If Microsoft is not configured, return the real config (it may have native auth)
    // so the app knows which methods are available.
    if (!config.client_id || !config.authority) {
      return { instance: null, config };
    }

    const appRedirectUri = config.redirect_uri;
    const surface = appSurfaceForPath(window.location.pathname);
    if (!isRedirectOriginTrusted(appRedirectUri)) {
      console.warn('Auth config has a missing or cross-origin redirect target');
      return { instance: null, config: { ...config, enabled: false } };
    }
    const authRedirectPath = surface === 'valentine_tasks'
      ? '/tasks-app/redirect.html'
      : '/redirect.html';
    const authRedirectUri = new URL(authRedirectPath, appRedirectUri).toString();
    const postLogoutRedirectUri = new URL(surfaceConfig(surface).basename, appRedirectUri).toString();

    const msalConfig: Configuration = {
      auth: {
        clientId: config.client_id,
        authority: config.authority,
        // MSAL Browser v5 requires auth responses to land on a minimal page
        // that runs its redirect bridge. Loading the full React application
        // here leaves silent-renewal iframes waiting until they time out.
        redirectUri: authRedirectUri,
        postLogoutRedirectUri,
      },
      cache: {
        cacheLocation: 'localStorage',
      },
    };

    const instance = new PublicClientApplication(msalConfig);
    await instance.initialize();

    // Process redirect response (returns auth result if coming back from Azure AD login)
    const interactiveLoginStarted = consumeInteractiveMicrosoftLoginStarted();
    const redirectResult = await instance.handleRedirectPromise();
    if (redirectResult?.account) {
      instance.setActiveAccount(redirectResult.account);
      if (interactiveLoginStarted && redirectResult.idToken) {
        completedInteractiveReauth.set(instance, redirectResult);
      }
    }

    return { instance, config };
  } catch (err) {
    console.error('MSAL initialization failed:', err);
    return { instance: null, config: { enabled: false } };
  }
}
